import { useEffect, useState, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Sheet, SheetContent } from '../ui/sheet';
import { Button } from '../ui/button';
import { SimpleTooltip } from '../ui/tooltip';
import { cn } from '../lib/utils';
import type { AdaptedNavGroup, AdaptedNavItem, ResolvedBranding, SidebarBadge } from './types';

interface SidebarProps {
  branding: ResolvedBranding;
  groups: AdaptedNavGroup[];
  mobileOpen: boolean;
  onMobileOpenChange: (open: boolean) => void;
  /** Optional per-item decorator returning a badge spec. */
  decorate?: (item: AdaptedNavItem) => SidebarBadge | undefined;
  /** Opt-in section collapse behavior. Defaults false for existing portal surfaces. */
  collapsibleGroups?: boolean;
  navGroupsInitiallyCollapsed?: boolean;
  navGroupStorageKey?: string;
  collapsibleSidebar?: boolean;
  sidebarStorageKey?: string;
  /** Optional footer node. Defaults to "v0.1.0 · admin". */
  footer?: ReactNode;
}

const DEFAULT_FOOTER = (
  <>
    <span className="font-medium text-foreground">v0.1.0</span> · admin
  </>
);

export function Sidebar({
  branding,
  groups,
  mobileOpen,
  onMobileOpenChange,
  decorate,
  collapsibleGroups = false,
  navGroupsInitiallyCollapsed = false,
  navGroupStorageKey,
  collapsibleSidebar = false,
  sidebarStorageKey,
  footer,
}: SidebarProps) {
  const [desktopCollapsed, setDesktopCollapsed] = useState(() => {
    if (!collapsibleSidebar || typeof window === 'undefined' || !sidebarStorageKey) return false;
    return window.localStorage.getItem(sidebarStorageKey) === 'collapsed';
  });

  useEffect(() => {
    if (!collapsibleSidebar || typeof window === 'undefined' || !sidebarStorageKey) return;
    window.localStorage.setItem(sidebarStorageKey, desktopCollapsed ? 'collapsed' : 'expanded');
  }, [collapsibleSidebar, desktopCollapsed, sidebarStorageKey]);

  const renderNavContent = (compact: boolean, showCollapseControl: boolean) => (
    <>
      <div
        className={cn(
          'flex items-center gap-2.5 border-b border-border px-4 py-[14px]',
          compact && 'justify-center px-2',
        )}
      >
        <div className="flex h-[30px] min-w-[30px] flex-shrink-0 items-center justify-center rounded-[8px] bg-primary px-1 text-[12px] font-extrabold tracking-[-1px] text-primary-foreground">
          {branding.iconText}
        </div>
        <div className={cn('text-[15px] font-bold leading-tight', compact && 'sr-only')}>
          {branding.shortLabel}
          {branding.shortSuffix && (
            <span className="ml-1 font-normal text-muted-foreground">{branding.shortSuffix}</span>
          )}
        </div>
      </div>
      <nav className="flex flex-1 flex-col gap-3 overflow-y-auto p-2 pb-[max(12px,env(safe-area-inset-bottom))]">
        {groups.map((group) => (
          <NavSection
            key={group.label}
            label={group.label}
            items={group.items}
            decorate={decorate}
            collapsible={collapsibleGroups || group.collapsible}
            initiallyOpen={!navGroupsInitiallyCollapsed}
            storageKey={navGroupStorageKey ? `${navGroupStorageKey}:${group.label}` : undefined}
            onNavigate={() => onMobileOpenChange(false)}
            compact={compact}
          />
        ))}
      </nav>
      <div
        className={cn(
          'flex items-center border-t border-border p-3 text-[11px] text-muted-foreground',
          compact ? 'justify-center' : 'justify-between gap-2',
        )}
      >
        {!compact && <div>{footer ?? DEFAULT_FOOTER}</div>}
        {collapsibleSidebar && showCollapseControl && (
          <SimpleTooltip text={desktopCollapsed ? 'Expand navigation' : 'Collapse navigation'}>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              onClick={() => setDesktopCollapsed((value) => !value)}
              aria-label={desktopCollapsed ? 'Expand navigation' : 'Collapse navigation'}
            >
              {desktopCollapsed ? (
                <ChevronRight className="h-4 w-4" aria-hidden />
              ) : (
                <ChevronLeft className="h-4 w-4" aria-hidden />
              )}
            </Button>
          </SimpleTooltip>
        )}
      </div>
    </>
  );

  return (
    <>
      <aside
        className={cn(
          'hidden h-screen flex-shrink-0 flex-col border-r border-border bg-card transition-[width] md:flex',
          desktopCollapsed ? 'w-[64px]' : 'w-[220px]',
        )}
      >
        {renderNavContent(desktopCollapsed, true)}
      </aside>
      <Sheet open={mobileOpen} onOpenChange={onMobileOpenChange}>
        <SheetContent side="left" className="w-[88vw] max-w-[320px] border-r p-0 md:hidden">
          {renderNavContent(false, false)}
        </SheetContent>
      </Sheet>
    </>
  );
}

function NavSection({
  label,
  items,
  decorate,
  collapsible,
  initiallyOpen,
  storageKey,
  onNavigate,
  compact = false,
}: {
  label: string;
  items: AdaptedNavItem[];
  decorate?: (item: AdaptedNavItem) => SidebarBadge | undefined;
  collapsible?: boolean;
  initiallyOpen?: boolean;
  storageKey?: string;
  onNavigate?: () => void;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(() => {
    if (!collapsible) return true;
    if (typeof window === 'undefined' || !storageKey) return initiallyOpen ?? true;
    const stored = window.sessionStorage.getItem(storageKey);
    if (stored === 'open') return true;
    if (stored === 'closed') return false;
    return initiallyOpen ?? true;
  });

  useEffect(() => {
    if (!collapsible || typeof window === 'undefined' || !storageKey) return;
    window.sessionStorage.setItem(storageKey, open ? 'open' : 'closed');
  }, [collapsible, open, storageKey]);

  return (
    <div>
      {compact ? (
        <div className="sr-only">{label}</div>
      ) : collapsible ? (
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="flex w-full items-center gap-1 px-2 pb-1.5 text-left text-[12px] font-extrabold uppercase tracking-[0.08em] text-primary transition-colors hover:text-primary/80"
          aria-expanded={open}
        >
          <ChevronDown
            className={cn('h-3 w-3 transition-transform', !open && '-rotate-90')}
            aria-hidden
          />
          <span>{label}</span>
        </button>
      ) : (
        <div className="px-2 pb-1.5 text-[12px] font-extrabold uppercase tracking-[0.08em] text-primary">
          {label}
        </div>
      )}
      <ul className={cn('flex flex-col gap-0.5', collapsible && !open && !compact && 'hidden')}>
        {items.map((item) => {
          const badge = decorate?.(item);
          const link = (
            <NavLink
              onClick={onNavigate}
              to={item.to}
              aria-label={compact ? item.label : undefined}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 rounded-md px-2 py-1.5 text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  compact && 'justify-center px-0 py-2',
                  isActive
                    ? 'bg-primary/10 font-medium text-primary'
                    : item.placeholder
                      ? 'text-amber-700 hover:bg-amber-50 hover:text-amber-900 dark:text-amber-300 dark:hover:bg-amber-950/30 dark:hover:text-amber-100'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon className="h-[15px] w-[15px] flex-shrink-0" aria-hidden />
                  {!compact && (
                    <span
                      className={cn(
                        item.placeholder &&
                          !isActive &&
                          'font-medium italic decoration-amber-700/40 underline-offset-2',
                      )}
                    >
                      {item.label}
                    </span>
                  )}
                  {typeof badge?.badge === 'number' && badge.badge > 0 && (
                    <span
                      className={cn(
                        compact
                          ? 'absolute ml-5 mt-[-18px] flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-medium tabular-nums'
                          : 'ml-auto rounded-full px-1.5 py-0.5 text-[10px] font-medium tabular-nums',
                        badge.badgeTone === 'destructive'
                          ? 'bg-destructive/15 text-destructive'
                          : isActive
                            ? 'bg-primary/15 text-primary'
                            : 'bg-muted text-muted-foreground',
                      )}
                    >
                      {badge.badge}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          );
          return (
            <li key={item.to} className="relative">
              {compact ? <SimpleTooltip text={item.label}>{link}</SimpleTooltip> : link}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
